import { defineStore } from "pinia";
import { useLocalStorage } from "@/helpers/localStorage";
import {useAppApi} from "@/composables/api/useAppApi";
import { useRouter } from "vue-router";

export interface AppStore {
  currentAppVersion: string | null;
  latestAppVersion: string | null;
  currentTicketingVersion: string | null;
  latestTicketingVersion: string | null;
  googleLoginLibraryFailed: boolean;
  useAppApi: any;
  router: object;
}

export const useAppStore = defineStore("app", {
  state: (): AppStore => ({
    currentAppVersion: null,
    latestAppVersion: null,
    currentTicketingVersion: null,
    latestTicketingVersion: null,
    googleLoginLibraryFailed: false,
    useAppApi: useAppApi(),
    router: useRouter(),

  }),
  getters: {
    // Use a getter to compare versions
    isCurrentVersionLatest: (state) => {
      return state.currentAppVersion === state.latestAppVersion;
    },
    isCurrentTicketingVersionLatest: (state) => {
      return state.currentTicketingVersion === state.latestTicketingVersion;
    },
  },
  actions: {
    initialiseStore() {
      // const {getItem} = useLocalStorage();
      // const currentAppVersion = getItem('app_version');
      //
      // // If the stored version is null, set the current version to latest
      // if (currentAppVersion == null) {
      //
      // } else {
      //   this.setCurrentAppVersion(currentAppVersion);
      // }

      //when we initialise the store the user must have the latest version
      this.setCurrentAppVersion(this.latestAppVersion);

      // // Check if the current version is the latest
      // if (!this.isCurrentVersionLatest) {
      //   this.handleVersionMismatch();
      // }

      // Set up a 5-minute interval to check for the latest app version
      this.scheduleVersionCheck();
    },

    setCurrentAppVersion(version: string | null) {
      this.currentAppVersion = version;
      const {setItem} = useLocalStorage();
      if (version) setItem('app_version', version);
    },

    setLatestAppVersion(version: string) {
      this.latestAppVersion = version;
    },

    handleVersionMismatch() {
      const {setItem} = useLocalStorage();
      if (this.latestAppVersion) {
        setItem('app_version', this.latestAppVersion);
        alert('A new version of the app is available. Your page will refresh to update.');
        window.location.reload(); // Force a hard reload
      }
    },

    async checkLatestVersion() {
      const {getLatestAppVersion} = this.useAppApi;
      try {
        // Call your API to get the latest app version
        const result = await getLatestAppVersion();

        // Ensure result is valid
        if (result && result.data && result.data.app_version) {
          // Update the latest app version
          this.setLatestAppVersion(result.data.app_version);

          // If current version is null (first time), just set it silently
          if (this.currentAppVersion === null) {
            this.setCurrentAppVersion(this.latestAppVersion);
          } else if (!this.isCurrentVersionLatest) {
            // Only show alert if versions don't match and current version is not null
            this.handleVersionMismatch();
          }
        }
      } catch (error) {
        console.error("Error fetching the latest app version:", error);
      }
    },

    scheduleVersionCheck() {
      var self = this;
      // Call the API every 5 minutes to check for the latest version
      setInterval(async () => {
        self.checkLatestVersion()
      }, 300000); // 300000 ms = 5 minutes
    },
    
    setCurrentTicketingVersion(version: string | null) {
      this.currentTicketingVersion = version;
      const {setItem} = useLocalStorage();
      if (version) setItem('ticketing_app_version', version);
    },
    
    setLatestTicketingVersion(version: string | null) {
      this.latestTicketingVersion = version;
    },
    
    handleTicketingVersionMismatch() {
      const {setItem} = useLocalStorage();
      if (this.latestTicketingVersion) {
        setItem('ticketing_app_version', this.latestTicketingVersion);
        alert('A new version of the ticketing app is available. Your page will refresh to update.');
        window.location.reload(); // Force a hard reload
      }
    },
    
    async checkTicketingVersion() {
      const {getLatestTicketingVersion} = this.useAppApi;
      try {
        // Call your API to get the latest ticketing app version
        const result = await getLatestTicketingVersion();
        
        // Ensure result is valid
        if (result && result.data && result.data.ticketing_app_version) {
          // Update the latest ticketing app version
          this.setLatestTicketingVersion(result.data.ticketing_app_version);
          
          // If current version is null (first time), just set it silently
          if (this.currentTicketingVersion === null) {
            this.setCurrentTicketingVersion(this.latestTicketingVersion);
          } else if (!this.isCurrentTicketingVersionLatest) {
            // Only show alert if versions don't match and current version is not null
            this.handleTicketingVersionMismatch();
          }
        }
      } catch (error) {
        console.error("Error fetching the latest ticketing app version:", error);
      }
    },
    
    initialiseTicketingVersionCheck() {
      // Get stored version from localStorage
      const {getItem} = useLocalStorage();
      const storedVersion = getItem('ticketing_app_version');
      if (storedVersion) {
        this.setCurrentTicketingVersion(storedVersion);
      }
      
      // Check version immediately
      this.checkTicketingVersion().catch(() => {
        // Silently fail - version check is not critical
      });
      
      // Set up a 5-minute interval to check for the latest ticketing version
      this.scheduleTicketingVersionCheck();
    },
    
    scheduleTicketingVersionCheck() {
      var self = this;
      // Call the API every 5 minutes to check for the latest ticketing version
      setInterval(async () => {
        self.checkTicketingVersion()
      }, 300000); // 300000 ms = 5 minutes
    },
    refreshAndRedirectToHome(message:string) {
      alert(message);
      (this.router as any).push('/');
    }
  }
});
